import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import {
  Background,
  BaseEdge,
  Controls,
  EdgeLabelRenderer,
  Handle,
  MarkerType,
  Position,
  ReactFlow,
  applyNodeChanges,
  useReactFlow,
  type Edge,
  type EdgeProps,
  type Node,
  type NodeChange,
  type NodeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import type { BoardCardView, WorkflowView } from '../../client/types.js';
import { formatTimestamp } from '../../logic/format.js';
import { projectWorkflowTasks, stageTaskOrder, workflowTaskPageLoadState } from '../../logic/workflow-task-order.js';
import { canonicalWorkflowLayout } from '../../logic/workflow-graph-layout.js';
import { useSaveWorkflowLayout, useWorkflowLayout } from '../queries.js';

interface StageNodeData extends Record<string, unknown> {
  readonly title: string;
  readonly kind: 'agent' | 'human' | 'terminal';
  readonly replicas: number;
  readonly checklist: boolean;
  readonly cards: readonly BoardCardView[];
  readonly total: number;
  readonly partial: boolean;
  readonly selectedCardId: string | null;
  readonly runningCardIds: ReadonlySet<string>;
  readonly onRoute: boolean;
  readonly active: boolean;
  readonly onOpen: (id: string) => void;
}

type StageNode = Node<StageNodeData, 'stage'>;

type Point = { readonly x: number; readonly y: number };
interface RoutedEdgeData extends Record<string, unknown> {
  readonly waypoints: readonly Point[];
  readonly editing: boolean;
  readonly onWaypointsChange: (edgeId: string, points: readonly Point[]) => void;
}
type RoutedEdge = Edge<RoutedEdgeData, 'routed'>;

function orthogonalVertices(
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  waypoints: readonly Point[],
): readonly Point[] {
  const anchors = waypoints.length > 0
    ? waypoints
    : [{ x: sourceX + (targetX - sourceX) / 2, y: sourceY + (targetY - sourceY) / 2 }];
  const vertices: Point[] = [{ x: sourceX, y: sourceY }];
  let currentY = sourceY;
  for (const point of anchors) {
    vertices.push({ x: point.x, y: currentY }, point);
    currentY = point.y;
  }
  vertices.push({ x: targetX, y: currentY }, { x: targetX, y: targetY });
  return vertices;
}

function orthogonalPath(vertices: readonly Point[]): string {
  const compact = vertices.filter((point, index) => {
    const previous = vertices[index - 1];
    return previous === undefined || previous.x !== point.x || previous.y !== point.y;
  });
  if (compact.length < 2) return '';
  const commands = [`M ${compact[0]!.x},${compact[0]!.y}`];
  for (let index = 1; index < compact.length - 1; index += 1) {
    const previous = compact[index - 1]!;
    const corner = compact[index]!;
    const next = compact[index + 1]!;
    const incoming = Math.abs(corner.x - previous.x) + Math.abs(corner.y - previous.y);
    const outgoing = Math.abs(next.x - corner.x) + Math.abs(next.y - corner.y);
    const radius = Math.min(12, incoming / 2, outgoing / 2);
    if (radius === 0) continue;
    const before = {
      x: corner.x - Math.sign(corner.x - previous.x) * radius,
      y: corner.y - Math.sign(corner.y - previous.y) * radius,
    };
    const after = {
      x: corner.x + Math.sign(next.x - corner.x) * radius,
      y: corner.y + Math.sign(next.y - corner.y) * radius,
    };
    commands.push(`L ${before.x},${before.y}`, `Q ${corner.x},${corner.y} ${after.x},${after.y}`);
  }
  const end = compact.at(-1)!;
  commands.push(`L ${end.x},${end.y}`);
  return commands.join(' ');
}

function pathCenter(vertices: readonly Point[]): Point {
  const lengths = vertices.slice(1).map((point, index) =>
    Math.abs(point.x - (vertices[index] as Point).x) + Math.abs(point.y - (vertices[index] as Point).y));
  const halfway = lengths.reduce((sum, length) => sum + length, 0) / 2;
  let travelled = 0;
  for (let index = 0; index < lengths.length; index += 1) {
    const length = lengths[index] as number;
    if (travelled + length >= halfway) {
      const start = vertices[index] as Point;
      const end = vertices[index + 1] as Point;
      const ratio = length === 0 ? 0 : (halfway - travelled) / length;
      return { x: start.x + (end.x - start.x) * ratio, y: start.y + (end.y - start.y) * ratio };
    }
    travelled += length;
  }
  return vertices.at(-1) as Point;
}

function waypointInsertionIndex(
  source: Point,
  target: Point,
  waypoints: readonly Point[],
  clicked: Point,
): number {
  if (waypoints.length === 0) return 0;
  const vertices: Point[] = [source];
  let currentY = source.y;
  for (const point of waypoints) {
    vertices.push({ x: point.x, y: currentY }, point);
    currentY = point.y;
  }
  vertices.push({ x: target.x, y: currentY }, target);
  let closestSegment = 0;
  let closestDistance = Number.POSITIVE_INFINITY;
  for (let index = 0; index < vertices.length - 1; index += 1) {
    const start = vertices[index] as Point;
    const end = vertices[index + 1] as Point;
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const lengthSquared = dx * dx + dy * dy;
    const ratio = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1,
      ((clicked.x - start.x) * dx + (clicked.y - start.y) * dy) / lengthSquared,
    ));
    const projectedX = start.x + ratio * dx;
    const projectedY = start.y + ratio * dy;
    const distance = (clicked.x - projectedX) ** 2 + (clicked.y - projectedY) ** 2;
    if (distance < closestDistance) {
      closestDistance = distance;
      closestSegment = index;
    }
  }
  return Math.min(waypoints.length, Math.floor(closestSegment / 2));
}

function RoutedWorkflowEdge(props: EdgeProps<RoutedEdge>): ReactNode {
  const { screenToFlowPosition } = useReactFlow();
  const savedWaypoints = props.data?.waypoints ?? [];
  const visibleWaypoints = savedWaypoints.length > 0
    ? savedWaypoints
    : [{ x: props.sourceX + (props.targetX - props.sourceX) / 2, y: props.sourceY + (props.targetY - props.sourceY) / 2 }];
  const vertices = orthogonalVertices(props.sourceX, props.sourceY, props.targetX, props.targetY, savedWaypoints);
  const path = orthogonalPath(vertices);
  const labelPosition = pathCenter(vertices);

  const startDragging = (index: number, event: ReactPointerEvent<HTMLButtonElement>): void => {
    if (!props.data?.editing) return;
    event.preventDefault();
    event.stopPropagation();
    const pointerId = event.pointerId;
    event.currentTarget.setPointerCapture(pointerId);
    const move = (moveEvent: PointerEvent): void => {
      const next = [...visibleWaypoints];
      next[index] = screenToFlowPosition({ x: moveEvent.clientX, y: moveEvent.clientY });
      props.data?.onWaypointsChange(props.id, next);
    };
    const stop = (): void => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', stop);
      window.removeEventListener('pointercancel', stop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', stop, { once: true });
    window.addEventListener('pointercancel', stop, { once: true });
  };

  const addWaypoint = (event: ReactMouseEvent<SVGPathElement>): void => {
    if (!props.data?.editing) return;
    event.preventDefault();
    event.stopPropagation();
    const clicked = screenToFlowPosition({ x: event.clientX, y: event.clientY });
    const insertionIndex = waypointInsertionIndex(
      { x: props.sourceX, y: props.sourceY },
      { x: props.targetX, y: props.targetY },
      savedWaypoints,
      clicked,
    );
    const next = [...savedWaypoints];
    next.splice(insertionIndex, 0, clicked);
    props.data.onWaypointsChange(props.id, next);
  };

  const removeWaypoint = (index: number, event: ReactMouseEvent<HTMLButtonElement>): void => {
    if (!props.data?.editing || savedWaypoints.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    props.data.onWaypointsChange(props.id, savedWaypoints.filter((_point, pointIndex) => pointIndex !== index));
  };

  return (
    <>
      <BaseEdge
        path={path}
        labelX={labelPosition.x}
        labelY={labelPosition.y}
        {...(props.label === undefined ? {} : { label: props.label })}
        {...(props.markerStart === undefined ? {} : { markerStart: props.markerStart })}
        {...(props.markerEnd === undefined ? {} : { markerEnd: props.markerEnd })}
        {...(props.style === undefined ? {} : { style: props.style })}
      />
      {props.data?.editing ? (
        <path className="workflow-edge-hit-area" d={path} onDoubleClick={addWaypoint} />
      ) : null}
      {props.data?.editing ? (
        <EdgeLabelRenderer>
          {visibleWaypoints.map((waypoint, index) => (
            <button
              type="button"
              key={`${props.id}:${String(index)}`}
              aria-label={`Bend ${String(index + 1)} for ${String(props.label ?? props.id)}`}
              title={savedWaypoints.length > 0 ? 'Drag to route · double-click to remove' : 'Drag to route'}
              className="workflow-edge-waypoint nodrag nopan"
              style={{ transform: `translate(-50%, -50%) translate(${waypoint.x}px, ${waypoint.y}px)` }}
              onPointerDown={(event) => startDragging(index, event)}
              onDoubleClick={(event) => removeWaypoint(index, event)}
            />
          ))}
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

function StageCardNode({ data, selected }: NodeProps<StageNode>): ReactNode {
  return (
    <article className={['workflow-node', `workflow-node-${data.kind}`, data.onRoute ? 'on-route' : '', data.active ? 'is-active' : '', selected ? 'selected' : ''].filter(Boolean).join(' ')}>
      <Handle type="target" position={Position.Left} isConnectable={false} />
      <header className="workflow-node-header">
        <span>{data.title}</span>
        <span
          className="workflow-node-count"
          title={data.partial ? 'Loaded tasks; more tasks are available' : 'Tasks'}
        >
          {data.partial ? `${data.total} loaded` : data.total}
        </span>
      </header>
      <p className="workflow-node-meta">
        {data.kind === 'agent' ? `${data.replicas} worker${data.replicas === 1 ? '' : 's'}` : data.kind}
        {data.checklist ? ' · checklist' : ''}
      </p>
      <div className="workflow-node-tasks">
        {data.cards.map((card) => (
          <button
            type="button"
            key={card.externalItemId}
            className={[
              'workflow-node-task',
              card.externalItemId === data.selectedCardId ? 'current' : '',
              data.runningCardIds.has(card.externalItemId) ? 'is-active' : '',
            ].filter(Boolean).join(' ')}
            onClick={() => data.onOpen(card.externalItemId)}
          >
            <span className="workflow-node-task-title">{card.title}</span>
            {data.runningCardIds.has(card.externalItemId) ? (
              <span className="workflow-task-spinner" role="img" aria-label="In progress" title="In progress" />
            ) : null}
          </button>
        ))}
        {data.total === 0 ? <span className="workflow-node-empty">No tasks</span> : null}
        {data.total > data.cards.length ? <span className="workflow-node-more">+{data.total - data.cards.length} more</span> : null}
      </div>
      <Handle type="source" position={Position.Right} isConnectable={false} />
    </article>
  );
}

const nodeTypes = { stage: StageCardNode };
const edgeTypes = { routed: RoutedWorkflowEdge };
const NODE_WIDTH = 250;
const NODE_HEIGHT = 190;
type SavedPositions = Readonly<Record<string, Point>>;
type SavedWaypoints = Readonly<Record<string, readonly Point[]>>;

function readSavedPositions(key: string, allowed: ReadonlySet<string>): SavedPositions {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '{}') as Record<string, { x?: unknown; y?: unknown }>;
    return Object.fromEntries(Object.entries(parsed).filter(([id, point]) =>
      allowed.has(id) && Number.isFinite(point.x) && Number.isFinite(point.y),
    )) as SavedPositions;
  } catch {
    return {};
  }
}

function readSavedWaypoints(key: string, allowed: ReadonlySet<string>): SavedWaypoints {
  try {
    const parsed = JSON.parse(localStorage.getItem(key) ?? '{}') as Record<string, unknown>;
    const result: Record<string, readonly Point[]> = {};
    for (const [id, value] of Object.entries(parsed)) {
      if (!allowed.has(id)) continue;
      const candidates = Array.isArray(value) ? value : [value];
      const points = candidates.filter((point): point is Point => {
        if (typeof point !== 'object' || point === null) return false;
        const candidate = point as { x?: unknown; y?: unknown };
        return Number.isFinite(candidate.x) && Number.isFinite(candidate.y);
      });
      if (points.length > 0) result[id] = points;
    }
    return result;
  } catch {
    return {};
  }
}

function primaryConfiguredRoute(graph: NonNullable<WorkflowView['graph']>, target: string): readonly string[] {
  const visit = (stage: string, route: readonly string[], seen: ReadonlySet<string>): readonly string[] => {
    if (stage === target) return route;
    // Preserve configuration order, but backtrack when its primary exit is a
    // dead end. The per-path seen set makes rework cycles harmless.
    for (const edge of graph.edges.filter((candidate) => candidate.from === stage)) {
      if (seen.has(edge.to)) continue;
      const found = visit(edge.to, [...route, edge.to], new Set([...seen, edge.to]));
      if (found.length > 0) return found;
    }
    return [];
  };
  return visit(graph.initialStage, [graph.initialStage], new Set([graph.initialStage]));
}

export function WorkflowGraphBoard({
  graph,
  cards,
  selectedCardId,
  stageFilter,
  onStageFilterChange,
  onClearSelection,
  onOpen,
  hasMore = false,
  stageTotals,
  onLoadMore,
  layoutKey,
}: {
  readonly graph: NonNullable<WorkflowView['graph']>;
  readonly cards: readonly BoardCardView[];
  readonly selectedCardId: string | null;
  readonly stageFilter: string | null;
  readonly onStageFilterChange: (stageId: string | null) => void;
  readonly onClearSelection: () => void;
  readonly onOpen: (id: string) => void;
  readonly hasMore?: boolean;
  readonly stageTotals?: Readonly<Record<string, number>>;
  readonly onLoadMore?: () => void;
  /** Team-scoped visual preference. It never changes executable workflow configuration. */
  readonly layoutKey: string;
}): ReactNode {
  const [showCompleted, setShowCompleted] = useState(false);
  const [visibleLimit, setVisibleLimit] = useState(100);
  const [editingLayout, setEditingLayout] = useState(false);
  const [editBaseRevision, setEditBaseRevision] = useState<number | null>(null);
  const [editTopologySignature, setEditTopologySignature] = useState<string | null>(null);
  const topologySignature = [
    'canonical-layout:v2',
    `initial:${graph.initialStage}`,
    `cancelled:${graph.cancelledStage}`,
    ...graph.nodes.map((node, index) => `n:${String(index)}:${node.id}:${node.kind}`),
    ...graph.edges.map((edge, index) => `e:${String(index)}:${edge.from}:${edge.from}->${edge.to}:${edge.outcome}`),
  ].join(',');
  const storageKey = `workflow-graph-layout:${layoutKey}:${topologySignature}`;
  const edgeStorageKey = `workflow-graph-edge-layout:${layoutKey}:${topologySignature}`;
  const sharedLayout = useWorkflowLayout(topologySignature);
  const saveSharedLayout = useSaveWorkflowLayout();
  const allowedNodeIds = useMemo(() => new Set(graph.nodes.map((node) => node.id)), [graph.nodes]);
  const allowedEdgeIds = useMemo(() => new Set(graph.edges.map((edge) => edge.id)), [graph.edges]);
  const [savedPositions, setSavedPositions] = useState<SavedPositions>(() => readSavedPositions(storageKey, allowedNodeIds));
  const [savedWaypoints, setSavedWaypoints] = useState<SavedWaypoints>(() => readSavedWaypoints(edgeStorageKey, allowedEdgeIds));
  useEffect(() => {
    if (editingLayout && editTopologySignature !== topologySignature) {
      setEditingLayout(false);
      setEditBaseRevision(null);
      setEditTopologySignature(null);
    }
  }, [editingLayout, editTopologySignature, topologySignature]);
  useEffect(() => {
    if (editingLayout) return;
    const remote = sharedLayout.data;
    setSavedPositions(remote?.positions ?? readSavedPositions(storageKey, allowedNodeIds));
    setSavedWaypoints(remote?.waypoints ?? readSavedWaypoints(edgeStorageKey, allowedEdgeIds));
  }, [allowedEdgeIds, allowedNodeIds, edgeStorageKey, editingLayout, sharedLayout.data, storageKey]);
  const selectedCard = cards.find((card) => card.externalItemId === selectedCardId) ?? null;
  const recordedRoute = selectedCard?.stagePosition?.visitedStageIds ?? [];
  const inferredRoute = selectedCard === null || recordedRoute.length > 1
    ? []
    : primaryConfiguredRoute(graph, selectedCard.workflowState);
  const displayedRoute = recordedRoute.length > 1 ? recordedRoute : inferredRoute;
  const displayedRouteKey = displayedRoute.join('|');
  const routeStages = new Set(displayedRoute);
  const traversed = new Set(displayedRoute.slice(1).map((to, index) => `${displayedRoute[index]}->${to}`));
  const rememberWaypoints = (edgeId: string, points: readonly Point[]): void => {
    setSavedWaypoints((current) => {
      return { ...current, [edgeId]: points };
    });
  };
  const automaticLayout = useMemo(
    () => canonicalWorkflowLayout(graph, NODE_WIDTH, NODE_HEIGHT),
    [graph],
  );
  const edges = useMemo<readonly RoutedEdge[]>(
    () =>
      graph.edges.map((edge) => ({
        id: edge.id,
        type: 'routed',
        source: edge.from,
        target: edge.to,
        label: edge.title,
        data: {
          waypoints: savedWaypoints[edge.id] ?? automaticLayout.waypoints[edge.id] ?? [],
          editing: editingLayout,
          onWaypointsChange: rememberWaypoints,
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          ...(traversed.has(`${edge.from}->${edge.to}`) ? { color: 'var(--borderColor-accent-emphasis)' } : {}),
        },
        className: traversed.has(`${edge.from}->${edge.to}`) ? 'workflow-edge workflow-edge-traversed' : 'workflow-edge',
        animated: false,
      })),
    [automaticLayout.waypoints, editingLayout, graph.edges, displayedRouteKey, savedWaypoints],
  );
  const cardsByStage = useMemo(() => {
    const grouped = new Map<string, BoardCardView[]>();
    for (const card of cards) {
      const group = grouped.get(card.workflowState) ?? [];
      group.push(card);
      grouped.set(card.workflowState, group);
    }
    for (const group of grouped.values()) group.sort(stageTaskOrder);
    return grouped;
  }, [cards]);
  const computedNodes = useMemo(() => {
    const raw = graph.nodes.map((stage): StageNode => {
      const current = cardsByStage.get(stage.id) ?? [];
      // The runtime capacity is authoritative. A stale board page must never
      // make one worker look as though it is executing several tasks at once.
      // Keep the newest acknowledged runs visible and present the rest as the
      // queue they effectively are.
      const runningCardIds = new Set(current
        .filter((card) => card.stagePosition?.isRunning)
        .sort((left, right) =>
          (right.stagePosition?.updatedAt ?? '').localeCompare(left.stagePosition?.updatedAt ?? ''))
        .slice(0, stage.replicas)
        .map((card) => card.externalItemId));
      return {
        id: stage.id,
        type: 'stage',
        position: { x: 0, y: 0 },
        initialWidth: NODE_WIDTH,
        initialHeight: NODE_HEIGHT,
        selected: stage.id === stageFilter || (stageFilter === null && stage.id === cards.find((card) => card.externalItemId === selectedCardId)?.workflowState),
        data: {
          title: stage.title,
          kind: stage.kind,
          replicas: stage.replicas,
          checklist: stage.checklistRef !== null,
          cards: current.slice(0, 5),
          total: stageTotals?.[stage.id] ?? current.length,
          partial: stageTotals === undefined && hasMore,
          selectedCardId,
          runningCardIds,
          onRoute: routeStages.has(stage.id),
          active: runningCardIds.size > 0,
          onOpen,
        },
      };
    });
    return raw.map((node) => ({
      ...node,
      position: savedPositions[node.id] ?? automaticLayout.positions[node.id] ?? node.position,
    }));
  }, [automaticLayout.positions, cards, cardsByStage, graph.nodes, hasMore, onOpen, savedPositions, selectedCardId, stageFilter, stageTotals]);
  const runningCardIds = useMemo(
    () => new Set(computedNodes.flatMap((node) => [...node.data.runningCardIds])),
    [computedNodes],
  );
  const [nodes, setNodes] = useState<StageNode[]>(() => [...computedNodes]);
  const draggingNode = useRef(false);
  useEffect(() => {
    if (draggingNode.current) return;
    setNodes((current) => {
      const previous = new Map(current.map((node) => [node.id, node]));
      return computedNodes.map((node) => {
        const measured = previous.get(node.id)?.measured;
        return measured === undefined ? node : { ...node, measured };
      });
    });
  }, [computedNodes]);
  const terminalStageIds = useMemo(
    () => new Set(graph.nodes.filter((stage) => stage.kind === 'terminal').map((stage) => stage.id)),
    [graph.nodes],
  );
  const totalCardCount = stageTotals === undefined
    ? cards.length
    : Object.values(stageTotals).reduce((sum, value) => sum + value, 0);
  const projection = projectWorkflowTasks(cards, terminalStageIds, stageFilter, showCompleted, selectedCardId, totalCardCount, stageTotals);
  const pageLoad = workflowTaskPageLoadState({
    loadedCardCount: cards.length,
    visibleCardCount: projection.visible.length,
    expectedVisibleCardCount: projection.expectedVisible,
    hasMore,
    showCompleted,
    filteredTotalIsUnknown: stageFilter !== null && stageTotals === undefined,
  });
  useEffect(() => {
    if (pageLoad.shouldLoad && onLoadMore !== undefined) onLoadMore();
  }, [onLoadMore, pageLoad.pageProgress, pageLoad.shouldLoad]);
  const rememberPosition = (node: StageNode): void => {
    if (!editingLayout) return;
    setSavedPositions((current) => {
      return { ...current, [node.id]: node.position };
    });
  };
  const updateNodes = (changes: readonly NodeChange<StageNode>[]): void => {
    const safeChanges = changes.filter((change) =>
      change.type === 'dimensions' || (editingLayout && change.type === 'position'),
    );
    if (safeChanges.length === 0) return;
    setNodes((current) => applyNodeChanges(safeChanges, current));
  };

  return (
    <section className="workflow-board" aria-label="Workflow graph">
      <aside className="workflow-task-browser">
        <header>
          <select aria-label="Filter tasks by stage" value={stageFilter ?? ''} onChange={(event) => { onStageFilterChange(event.currentTarget.value || null); setVisibleLimit(100); }}>
            <option value="">All tasks</option>
            {graph.nodes.map((node) => <option key={node.id} value={node.id}>{node.title}</option>)}
          </select>
          <span className="workflow-task-browser-count">{projection.expectedVisible}</span>
        </header>
        <label className="workflow-task-browser-filter">
          <input type="checkbox" checked={showCompleted} onChange={(event) => setShowCompleted(event.currentTarget.checked)} />
          Show completed
          {!showCompleted && stageFilter === null ? <span>{projection.hidden} hidden</span> : null}
        </label>
        <div className="workflow-task-list">
          {projection.visible.slice(0, visibleLimit).map((card) => (
            <button
              type="button"
              key={card.externalItemId}
              className={[
                'workflow-task-row',
                card.externalItemId === selectedCardId ? 'selected' : '',
                runningCardIds.has(card.externalItemId) ? 'is-active' : '',
              ].filter(Boolean).join(' ')}
              onClick={() => onOpen(card.externalItemId)}
            >
              <strong>{card.title}</strong>
              <span>
                {graph.nodes.find((stage) => stage.id === card.workflowState)?.title ?? card.workflowState}
                {card.stagePosition == null ? '' : ` · ${card.stagePosition.phase === 'in_progress' ? 'In progress' : card.stagePosition.phase === 'done' ? 'Done' : card.stagePosition.eligibility === 'dependency_blocked' ? 'Blocked' : 'In queue'}`}
              </span>
              <time dateTime={card.createdAt}>Created {formatTimestamp(card.createdAt)}</time>
            </button>
          ))}
          {projection.visible.length > visibleLimit ? (
            <button type="button" className="link-button" onClick={() => setVisibleLimit((value) => value + 100)}>
              Show 100 more
            </button>
          ) : null}
          {showCompleted && projection.visible.length <= visibleLimit && hasMore && onLoadMore !== undefined ? (
            <button type="button" className="link-button" onClick={onLoadMore}>Load older completed tasks</button>
          ) : null}
        </div>
      </aside>
      <div className="workflow-canvas">
        {selectedCard === null ? null : (
          <p className="workflow-route-note">
            {recordedRoute.length > 1
              ? 'Recorded task route'
              : inferredRoute.length > 1
                ? 'Configured route · history unavailable for this legacy task'
                : 'No recorded route for this task'}
          </p>
        )}
        <div className="workflow-layout-controls">
          {editingLayout ? (
            <>
              <span>Drag nodes or bends · double-click a line to add a bend</span>
              <button type="button" onClick={() => {
                setSavedPositions({});
                setSavedWaypoints({});
              }}>Auto arrange</button>
              <button type="button" disabled={saveSharedLayout.isPending} onClick={() => {
                void saveSharedLayout.mutateAsync({
                  topologySignature: editTopologySignature ?? topologySignature,
                  positions: savedPositions,
                  waypoints: savedWaypoints,
                  expectedRevision: editBaseRevision ?? 0,
                }).then((saved) => {
                  setSavedPositions(saved.positions);
                  setSavedWaypoints(saved.waypoints);
                  try { localStorage.removeItem(storageKey); } catch { /* The shared copy is authoritative. */ }
                  try { localStorage.removeItem(edgeStorageKey); } catch { /* The shared copy is authoritative. */ }
                  setEditingLayout(false);
                  setEditBaseRevision(null);
                  setEditTopologySignature(null);
                }).catch(() => { /* The mutation exposes the error beside the controls. */ });
              }}>{saveSharedLayout.isPending ? 'Saving…' : 'Done'}</button>
            </>
          ) : (
            <button type="button" disabled={sharedLayout.isLoading} onClick={() => {
              setEditBaseRevision(sharedLayout.data?.revision ?? 0);
              setEditTopologySignature(topologySignature);
              setEditingLayout(true);
            }}>{sharedLayout.isLoading ? 'Loading layout…' : 'Edit layout'}</button>
          )}
        </div>
        {saveSharedLayout.isError ? <p className="error-row">Could not save the shared layout: {saveSharedLayout.error.message}</p> : null}
        <ReactFlow
          nodes={[...nodes]}
          edges={[...edges]}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={editingLayout}
          onNodesChange={updateNodes}
          onNodeDragStart={() => { draggingNode.current = true; }}
          onNodeDragStop={(_event, node) => {
            draggingNode.current = false;
            rememberPosition(node);
          }}
          nodesConnectable={false}
          edgesReconnectable={false}
          deleteKeyCode={null}
          fitView
          fitViewOptions={{ padding: 0.16 }}
          minZoom={0.25}
          maxZoom={1.5}
          onNodeClick={(_event, node) => { onStageFilterChange(node.id); setVisibleLimit(100); }}
          onPaneClick={onClearSelection}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={22} size={1} />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
    </section>
  );
}
